import React from "react";
import PropTypes from "prop-types";
import Chart from "react-apexcharts";
import { automationTestIds } from "../../testing/automationTestIds";
import { cssVar, DASHBOARD_CHART_CSS_VARS } from "../../utils/cssVariables";

export const dashboardPieChartId = automationTestIds.dashboardChartPie;
export const PIE_CHART_HEIGHT = 300;

export const PIE_CHART_LEGEND_ITEMS = [
  { colorClass: "bg-cfDashboardChartIssued", labelKey: "dashboard:issued" },
  { colorClass: "bg-cfDashboardChartRevoked", labelKey: "dashboard:revoked" },
  { colorClass: "bg-cfDashboardChartFailed", labelKey: "dashboard:failed" },
  { colorClass: "bg-cfDashboardChartExpired", labelKey: "dashboard:expired" },
];

export const getPieChartOptions = (t) => {
  return {
    labels: [
      t("dashboard:issued"),
      t("dashboard:revoked"),
      t("dashboard:failed"),
      t("dashboard:expired"),
    ],
    colors: [
      cssVar(DASHBOARD_CHART_CSS_VARS.credentialIssued),
      cssVar(DASHBOARD_CHART_CSS_VARS.credentialRevoked),
      cssVar(DASHBOARD_CHART_CSS_VARS.credentialFailed),
      cssVar(DASHBOARD_CHART_CSS_VARS.credentialExpired),
    ],
    chart: {
      type: "pie",
      width: "100%",
      height: PIE_CHART_HEIGHT,
      fontFamily: "Inter, Helvetica, Arial, sans-serif",
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 700,
      },
    },
    states: {
      hover: {
        filter: {
          type: "lighten",
          value: 0.08,
        },
      },
      active: {
        filter: {
          type: "darken",
          value: 0.12,
        },
      },
    },
    legend: {
      show: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: [cssVar(DASHBOARD_CHART_CSS_VARS.sliceBorder)],
    },
    dataLabels: {
      enabled: true,
      formatter: function (value) {
        if (!value || value < 0.5) {
          return "";
        }
        return parseFloat(value).toFixed(1) + "%";
      },
      style: {
        fontSize: "13px",
        fontFamily: "Inter, Helvetica, Arial, sans-serif",
        fontWeight: 600,
        colors: [cssVar(DASHBOARD_CHART_CSS_VARS.dataLabelText)],
      },
      dropShadow: {
        enabled: false,
      },
    },
    plotOptions: {
      pie: {
        expandOnClick: true,
        customScale: 1,
        dataLabels: {
          offset: -2,
          minAngleToShowLabel: 12,
        },
      },
    },
    tooltip: {
      enabled: true,
      theme: "dark",
      style: {
        fontSize: "13px",
        fontFamily: "Inter, Helvetica, Arial, sans-serif",
      },
      y: {
        formatter: function (value) {
          return parseFloat(value).toFixed(1) + "%";
        },
      },
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          chart: {
            height: 280,
          },
        },
      },
      {
        breakpoint: 640,
        options: {
          chart: {
            height: 260,
          },
          plotOptions: {
            pie: {
              dataLabels: {
                offset: 0,
              },
            },
          },
        },
      },
    ],
  };
};

export const getPieChartSeries = (credentialDistribution) => {
  return [
    parseFloat((credentialDistribution?.issued || 0).toFixed(1)),
    parseFloat((credentialDistribution?.revoked || 0).toFixed(1)),
    parseFloat((credentialDistribution?.failed || 0).toFixed(1)),
    parseFloat((credentialDistribution?.expired || 0).toFixed(1)),
  ];
};

export const DashboardPieChart = ({ options, series, className, chartKey }) => (
  <div id={dashboardPieChartId}>
    <Chart
      key={chartKey}
      options={options}
      series={series}
      type="pie"
      className={className}
    />
  </div>
);

DashboardPieChart.propTypes = {
  options: PropTypes.object.isRequired,
  series: PropTypes.array.isRequired,
  className: PropTypes.string,
  chartKey: PropTypes.string,
};

DashboardPieChart.defaultProps = {
  className: "",
  chartKey: undefined,
};
